import { queryOptions, useQuery } from '@tanstack/react-query';

import { apiClient } from '@cast/infrastructure-api';
import { SpotInterruptionStep } from 'features/cluster/spot-interruptions/_utils/getTimeSeriesGranularity';

const SPOT_INTERRUPTION_PREDICTION_PERFORMANCE_METRICS_QUERY_KEY =
  'spot-interruption-prediction-performance-metrics';

type UseSpotInterruptionPredictionPerformanceMetricsQueryParams = {
  organizationId?: string;
  clusterIds: string[];
  key: string;
  startTime: string;
  endTime: string;
  timeZone: string;
  step?: SpotInterruptionStep;
  nodeTemplateNames?: string[];
  enabled?: boolean;
};

const getSpotInterruptionPredictionPerformanceMetricsQueryKey = ({
  organizationId,
  clusterIds,
  key,
  step,
  nodeTemplateNames,
}: Pick<
  UseSpotInterruptionPredictionPerformanceMetricsQueryParams,
  'organizationId' | 'clusterIds' | 'key' | 'step' | 'nodeTemplateNames'
>) =>
  [
    SPOT_INTERRUPTION_PREDICTION_PERFORMANCE_METRICS_QUERY_KEY,
    organizationId,
    clusterIds,
    key,
    step,
    nodeTemplateNames,
  ] as const;

const getSpotInterruptionPredictionPerformanceMetricsQueryOptions = ({
  organizationId,
  clusterIds,
  key,
  startTime,
  endTime,
  timeZone,
  step,
  nodeTemplateNames,
  enabled = true,
}: UseSpotInterruptionPredictionPerformanceMetricsQueryParams) =>
  queryOptions({
    queryKey: getSpotInterruptionPredictionPerformanceMetricsQueryKey({
      organizationId,
      clusterIds,
      key,
      step,
      nodeTemplateNames,
    }),
    queryFn: async () => {
      const { data } =
        await apiClient.costReport.getSpotInterruptionPredictionPerformanceMetrics(
          {
            organizationId: organizationId!,
            clusterIds,
            startTime,
            endTime,
            timeZone,
            step,
            nodeTemplateNames,
          }
        );

      return data;
    },
    enabled:
      enabled &&
      !!organizationId &&
      clusterIds.length > 0 &&
      !!startTime &&
      !!endTime,
  });

export const useSpotInterruptionPredictionPerformanceMetricsQuery = (
  props: UseSpotInterruptionPredictionPerformanceMetricsQueryParams
) => {
  return useQuery(
    getSpotInterruptionPredictionPerformanceMetricsQueryOptions(props)
  );
};
